import type { MoreMenuCategory } from '@odo/shared-ui';
import { statusCategory, tagsCategory, useInboxBarFilters } from '../shared/inboxFilters';
import { LEVEL_FILTER_OPTIONS } from './dmNotificationLevels';
import { DEFAULT_FOLD_MODE, FOLD_MODE_OPTIONS, parseFoldMode } from './inboxFolds';
import { useNotificationApiFilters } from './useNotificationApiFilters';
import type { useNotificationListUrlState } from './useNotificationListUrlState';

type NotificationListTable = ReturnType<typeof useNotificationListUrlState>;

function moreCategories(table: NotificationListTable): MoreMenuCategory[] {
    const { levels, tags } = table.filters;
    return [
        statusCategory(table.status, table.setStatus),
        {
            key: 'levels',
            label: 'Level',
            options: LEVEL_FILTER_OPTIONS,
            selectedOptions: levels,
            onChange: (value) => table.setFilter('levels', value)
        },
        tagsCategory(tags, (value) => table.setFilter('tags', value)),
        {
            key: 'rows',
            label: 'Rows',
            options: FOLD_MODE_OPTIONS,
            selectedOptions: [table.rows],
            onChange: ([value]) => table.setRows(parseFoldMode(value ?? null)),
            selection: 'single',
            defaultValue: DEFAULT_FOLD_MODE
        }
    ];
}

export function useNotificationFilters(table: NotificationListTable) {
    const { levels, practiceIds, assignees, tags } = table.filters;
    const { setFilter } = table;
    const { apiFilters, assigneeOptions, assigneesResolved } = useNotificationApiFilters(
        {
            search: table.search,
            levels,
            practiceIds,
            assignees,
            tags,
            status: table.status,
            sort: table.sort,
            order: table.order
        },
        (values) => setFilter('assignees', values)
    );
    const filters = useInboxBarFilters({ assigneeOptions, assignees, practiceIds, setFilter });
    // Status and Rows always have a choice ticked, so they only count once off their default.
    const moreActiveCount = [
        table.status === 'closed',
        levels.length > 0,
        tags.length > 0,
        table.rows !== DEFAULT_FOLD_MODE
    ].filter(Boolean).length;
    return { filters, more: moreCategories(table), moreActiveCount, apiFilters, assigneesResolved };
}
